import { nanoid } from 'nanoid';
import { deepCloneObject } from '../utility';
import {
	CELL_KEYS,
	IDENTIFIER_TYPES,
} from '../../pages/RequestFlow/RequesteeSearch/_constants';

/**
 * Helper function.
 * Conditionally clones and updates @param actions if the header action cell of index @param index
 * does not reflect the state of action cells in @param rows .
 * A header action cell should only be `Checked` if all non-header, valid action cells are `Checked`.
 *
 * @function
 * @param {Object[]} actions `actions` array in state.
 * @param {*} rows `rows` array in state.
 * @param {*} index bound `actions` index defining action of interest.
 * @returns conditionally cloned and updated array of action objects.
 */
export const updateHeaderActionCell = (actions, rows, index) => {
	let actionsUpdated = actions;
	const areActionCellsChecked = rows
		.filter(row => checkValidity(row) === true)
		.every(row => row.ActionCells[index].Checked === true);
	if (actions[0] && actions[0].Header?.Checked !== areActionCellsChecked) {
		actionsUpdated = deepCloneObject(actions);
		actionsUpdated[0].Header.Checked = areActionCellsChecked;
	}
	return actionsUpdated;
};

/**
 * Helper function.
 * Checks value(s) of cell(s) or existence of optional cell(s) contributing to row's definition of validity.
 *
 * @param {Object} row `DataTable` `row` object of interest.
 * @returns {Boolean} indication of validity.
 */
export const checkValidity = row => {
	const keysCell = Object.keys(row.Cells);
	return (
		Object.values(IDENTIFIER_TYPES).every(identifierType => {
			const identifierKey = identifierType.replace(/ /g, '_');
			if (
				identifierKey in row.Cells &&
				row.Cells[identifierKey].Validation &&
				row.Cells[identifierKey].Validation.Key
			) {
				return (
					row.Cells[identifierKey].Validation.Key.toLowerCase() ===
					'true'
				);
			}
			return true;
		}) &&
		(!keysCell.includes(CELL_KEYS.RETIRED) ||
			row.Cells[CELL_KEYS.RETIRED].Validation.Key.toLowerCase() ===
				'true') &&
		!keysCell.includes(CELL_KEYS.PENDING) &&
		!keysCell.includes(CELL_KEYS.DISABLED) &&
		!keysCell.includes(CELL_KEYS.RESTRICTED) &&
		!keysCell.includes(CELL_KEYS.DENIED)
	);
};

/**
 * Helper function.
 * Creates custom object from @param row for saving as requestee selection.
 * `Name` cell first index in array so that its value renders as `Chip` label in `Multiselect`.
 *
 * @function
 * @param {Object} row `DataTable` `row` object of interest.
 * @returns {Object} custom object to push to array of selected requestees.
 */
export const createRequestee = row => {
	const keys = [
		'Name',
		'Network_ID',
		'Job_Title',
		'Department',
		'UPMC_E-Sign-On_ID',
		'Manager',
		'Employee_Status',
		CELL_KEYS.UNASSOCIATED,
		CELL_KEYS.PENDING,
		CELL_KEYS.UNACKNOWLEDGED,
		'FI_ID',
	];
	let objectRequestee = {};
	const arrRowKeys = Object.keys(row.Cells);
	keys.forEach(key => {
		if (arrRowKeys.includes(key)) {
			objectRequestee[key] = deepCloneObject(row.Cells[key]);
			if (key === 'FI_ID') {
				objectRequestee.Key = row.Cells['FI_ID'].Value;
			}
		}
	});
	if (!objectRequestee.Key) {
		objectRequestee.Key = nanoid();
	}
	objectRequestee.isSelected = true;
	return objectRequestee;
};
